import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { primaryImage, type Experience } from '../data/experiences';
import { imgDims } from '../data/imageMeta';
import { useBooking } from './booking';
import { useExperiences } from '../hooks';

export function ExperienceCard({
  exp,
  tall = false,
  imageOverride,
}: {
  exp: Experience;
  tall?: boolean;
  /** Use a different card image than `images[0]` (e.g. the home page hero card). */
  imageOverride?: string;
}) {
  const image = imageOverride ?? primaryImage(exp);
  const { width, height } = imgDims(image);
  const { openBooking } = useBooking();
  const { byAlias } = useExperiences();
  // Live "from" price only — no static fallback.
  const dyn = byAlias(exp.productAlias);
  const fromPrice = dyn?.startingDiscountedPrice ?? dyn?.startingPrice ?? null;
  return (
    <article className="group flex flex-col overflow-hidden rounded-2xl bg-white shadow-card transition-all duration-500 hover:-translate-y-1 hover:shadow-cardHover">
      {/* Image */}
      <Link
        to={`/${exp.slug}`}
        aria-label={`View ${exp.title}`}
        className={`relative block overflow-hidden ${tall ? 'aspect-[4/5]' : 'aspect-[16/11]'}`}
      >
        <img
          src={image}
          alt={exp.title}
          loading="lazy"
          width={width}
          height={height}
          className="h-full w-full object-cover transition-transform duration-[1.2s] ease-out group-hover:scale-105"
        />
        {exp.badge && (
          <span className="absolute left-4 top-4 rounded-full bg-crimson px-3 py-1 font-sans text-[0.6rem] font-semibold uppercase tracking-wide2 text-white">
            {exp.badge}
          </span>
        )}
      </Link>

      {/* Body */}
      <div className="flex flex-1 flex-col items-center px-6 pb-6 pt-7 text-center sm:px-8">
        <p className="font-sans text-[0.65rem] font-medium uppercase tracking-label text-crimson">
          {exp.kicker}
        </p>
        <h3 className="mt-2 font-display text-[1.6rem] font-medium text-charcoal">
          <Link to={`/${exp.slug}`} className="transition-colors hover:text-crimson">
            {exp.title}
          </Link>
        </h3>
        <p className="mt-2 max-w-xs font-sans text-sm font-light leading-relaxed text-stone">
          {exp.cardDescription}
        </p>

        {/* Starting price (live only) */}
        {fromPrice != null && (
          <p className="mt-4 font-sans text-[0.7rem] font-medium uppercase tracking-wide2 text-charcoal/55">
            From{' '}
            <span className="font-display text-lg normal-case tracking-normal text-charcoal">
              &euro;{fromPrice}
            </span>
          </p>
        )}

        {/* Audience */}
        <ul className="mt-6 flex items-start justify-center gap-8">
          {exp.audience.map(({ label, Icon }) => (
            <li key={label} className="flex flex-col items-center gap-2">
              <Icon className="h-[1.35rem] w-[1.35rem] text-crimson" strokeWidth={1.6} />
              <span className="font-sans text-[0.6rem] font-medium uppercase tracking-wide2 text-charcoal/70">
                {label}
              </span>
            </li>
          ))}
        </ul>

        {/* Actions */}
        <div className="mt-7 flex w-full items-center justify-between border-t border-charcoal/10 pt-5">
          <Link
            to={`/${exp.slug}`}
            className="group/link inline-flex items-center gap-2 font-sans text-[0.7rem] font-medium uppercase tracking-wide2 text-charcoal transition-colors hover:text-crimson"
          >
            View Details
            <ArrowRight className="h-4 w-4 transition-transform group-hover/link:translate-x-1" />
          </Link>
          <button
            type="button"
            onClick={() => openBooking(exp, undefined, 'experience_card')}
            className="btn btn-primary px-5 py-2 text-[0.65rem]"
          >
            Book Now
          </button>
        </div>
      </div>
    </article>
  );
}
